import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import { Alert, Card, CardBody, CardHeader, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { Afr, getSelectedAfr, setToImportedAfr, Table } from '../../redux/AfrSlice';
import {
    AFRDocumentStarter, AfrImportSender, DocumentReference, FileSender, getAfrInstance, getTaxonomy,
    getTemplate, importAfrInstanceFile, importFinancialReportSpreadsheetFile, sendFile
} from '../../redux/DocumentsSlice';
import { Entity, getSelectedEntity } from '../../redux/EntitySlice';
import { getDimensionSectionMap, getWorkflowState, isDirty, setIsDirty, setWorkflowStatus, Workflow } from '../../redux/MiscSlice';
import { GenericActionTile } from '../core/GenericActionTile';
import { GenericActionTileUploadFile } from '../core/GenericActionTileUploadFile';
import { ListItem } from '../../redux/VisualizationSlice';
import { defaultReportFormatValue, IReportFormat } from '../feature/SystemReports';
import Swal from 'sweetalert2';
import utilityHelper from '../../utils/utilityHelper';
import { getValidationsForAfr, validateAfr, ValidationContext } from '../../redux/ValidationSlice';
import { getSelectedEvents } from '../../redux/EventsSlice';
import UserRoleEnum from '../../redux/UserSlice';


export const downloadFormats: Array<IReportFormat> = [
    { label: 'Excel', value: 'EXCEL' },
    { label: 'PDF', value: 'PDF' },
    { label: 'XBRL', value: 'XBRL' }];



const CR29ImportExportCore = (props: { userRole: string }) => {

    const dispatch = useAppDispatch();

    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const afrYear = currentAfr ? currentAfr.year : 0;
    const logicalPeriod = currentAfr ? currentAfr.logicalPeriod : "ANNUAL";
    const entity: Entity | null = useAppSelector(getSelectedEntity);
    const dMap = useAppSelector(getDimensionSectionMap);
    const currentEvents = useAppSelector(getSelectedEvents);

    // Hooks
    const [file, setFile] = React.useState<File | null>(null);
    const [templatePopulated, setTemplatePopulated] = React.useState<"false" | "true">("false");
    const [selectedReportFormat, setSelectedReportFormat] = React.useState<IReportFormat>(defaultReportFormatValue);
    const [availableSections, setAvailableSections] = React.useState<Array<ListItem>>([]);
    const [sectionChoices, setSectionChoices] = React.useState<string[]>([]);
    const isDirtyInfo: boolean = useAppSelector(isDirty);

    const [isDownloadingAfrTemplate, setIsDownloadingAfrTemplate] = React.useState<boolean>(false);
    const [isDownloadingInstance, setIsDownloadingInstance] = React.useState<boolean>(false);
    const [isDownloadingTaxonomy, setIsDownloadingTaxonomy] = React.useState<boolean>(false);
    const [isUploadingInstance, setIsUploadingInstance] = React.useState<boolean>(false);
    const [isUploadingSpreadsheet, setIsUploadingSpreadsheet] = React.useState<boolean>(false);
    const [isDownloadingCurrentAfr, setIsDownloadingCurrentAfr] = React.useState<boolean>(false);


    const currentWF: Workflow | null = useAppSelector(getWorkflowState);

    // NOTE - workflowStates needed to be properly typed in order for my somewhat older verison of Typescript to accept statements like 
    //               workflowStates[step]
    // as unambiguously returning a string
    const workflowStates: { [index: string]: string } = {
        // Excel WF
        RESET_EXCEL: "EXCELSequence|1",
        DOWNLOAD_TEMPLATE: "EXCELSequence|2",
        UPLOAD_SPREADSHEET: "EXCELSequence|3",
        DOWNLOAD_DATA: "EXCELSequence|1",

        // XBRL WF
        RESET_XBRL: "XBRLSequence|1",
        DOWNLOAD_INSTANCE: "XBRLSequence|2",
        UPLOAD_INSTANCE: "XBRLSequence|1",
    }

    const getWorklowState = function (step: string): string {
        if (!workflowStates.hasOwnProperty(step)) {
            // Set as default
            alert("defaulting: " + step)
            return "EXCELSequence|1";
        }
        return workflowStates[step];
    }

    const setWFStep = function (step: string) {
        var newWF = JSON.parse(JSON.stringify(currentWF))
        var wfState: string = workflowStates[step];
        var WF = wfState.split("|")[0];
        var sequence = wfState.split("|")[1];

        newWF[WF] = parseInt(sequence);
        // send it to redux
        dispatch(setWorkflowStatus(newWF));
    }

    const canImportInstance = function (dirtyState: boolean, userRole: string): boolean {
        if (dirtyState) {
            return false;
        }
        if (userRole === "ADMIN") {
            return true;
        }
        return false;

    }
    const importExportAction = async function (atype: string, ename: string, ecode: string, yr: number) {

        switch (atype) {
            case "DOWNLOAD_TAXONOMY":
                setIsDownloadingTaxonomy(true);
                await downloadTaxonomy(atype);
                setIsDownloadingTaxonomy(false);
                break;

            case "DOWNLOAD_TEMPLATE":
                setIsDownloadingAfrTemplate(true);
                await downloadDocumentTemplate(atype, templatePopulated === "true" ? 'PRIOR_YEAR' : 'NO');
                setWFStep(atype);
                setIsDownloadingAfrTemplate(false);
                break;

            case "DOWNLOAD_INSTANCE":
                setIsDownloadingInstance(true);
                await downloadAfrInstance(atype, 'PRIOR_YEAR');
                setWFStep(atype);
                setIsDownloadingInstance(false);
                break;

            case "DOWNLOAD_CURRENT_AFR":
                setIsDownloadingCurrentAfr(true);
                switch (selectedReportFormat.value) {
                    case "EXCEL":
                        await downloadDocumentTemplate(atype, 'CURRENT_YEAR');
                        break;

                    case "XBRL":
                        await downloadAfrInstance(atype, 'CURRENT_YEAR');
                        break;

                }
                setIsDownloadingCurrentAfr(false);
                break;
        }
    }

    const importExportActionUploadedFile = function (atype: string, ename: string, ecode: string, yr: number, uploadedFile: File) {
        setFile(uploadedFile);
        switch (atype) {
            case "UPLOAD_SPREADSHEET":
            case "UPLOAD_INSTANCE":
                importAfr(atype, uploadedFile);
                setWFStep(atype);
                break;
        }

    }

    // reset workflow
    const handleClick = function (event: any) {
        setWFStep(event.target.name);
    }

    function getAfrData(docType: string, bPopulate: 'NO' | 'CURRENT_YEAR' | 'PRIOR_YEAR') {
        if (!currentAfr || !entity) {
            return null;
        }
        const afrData: AFRDocumentStarter = {
            afrYear: currentAfr.year,
            entityCode: entity.code,
            fileType: docType,
            outputType: null,
            populate: bPopulate
        }

        return afrData;
    }

    async function downloadDocumentTemplate(docType: string, bPopulate: 'NO' | 'CURRENT_YEAR' | 'PRIOR_YEAR') {
        var request = getAfrData(docType, bPopulate);
        if (!request) {
            alert("Must have a selected entity and AFR!");
            return;
        }
        const response = await dispatch(getTemplate(request));
        const doc = unwrapResult(response);
        if (doc !== null) {
            await downloadDocumentTemplateToBrowser(doc);
        }
    }

    async function downloadAfrInstance(docType: string, populateOption: 'NO' | 'CURRENT_YEAR' | 'PRIOR_YEAR') {
        var request = getAfrData(docType, populateOption);
        if (!request) {
            alert("Must have a selected entity and AFR!");
            return;
        }
        const response = await dispatch(getAfrInstance(request));
        const doc = unwrapResult(response);
        if (doc !== null) {
            await downloadAfrInstanceToBrowser(doc);
        }
    }

    async function downloadTaxonomy(docType: string) {
        if (!currentAfr || !entity) {
            alert("Must have a selected AFR!");
            return;
        }
        const request: AFRDocumentStarter = {
            afrYear: currentAfr.year,
            entityCode: entity.code,
            fileType: "TAXONOMY",
            outputType: null,
            populate: 'NO'
        }
        const response = await dispatch(getTaxonomy(request));
        const doc = unwrapResult(response);
        if (doc !== null) {
            await downloadTaxonomyToBrowser(doc);
        }
    }

    async function uploadDocument(docType: string) {
        const uploadedFile = file;
        if (uploadedFile !== null &&
            docType !== null) {
            let fileSender: FileSender = { file: uploadedFile, fileType: docType };
            const response = await dispatch(sendFile(fileSender));
            const doc = unwrapResult(response);
        }
    }

    async function importAfr(docType: string, uploadedFile: File) {

        if (entity && currentAfr && uploadedFile !== null &&
            docType !== null) {

            let fileSender: AfrImportSender = { entityCode: currentAfr.afrId, afrYear: currentAfr.year, file: uploadedFile, fileType: docType, sections: [] };
            var response = null;
            switch (docType) {
                case "UPLOAD_SPREADSHEET":
                    setIsUploadingSpreadsheet(true);
                    //if there are any section qualifiers - add them here, they only apply to spreadsheet uploads
                    if (sectionChoices.length > 0) {
                        fileSender.sections = sectionChoices;
                    }
                    response = await dispatch(importFinancialReportSpreadsheetFile(fileSender));
                    setIsUploadingSpreadsheet(false);
                    break;
                case "UPLOAD_INSTANCE":
                    setIsUploadingInstance(true);
                    response = await dispatch(importAfrInstanceFile(fileSender));
                    setIsUploadingInstance(false);
                    break;

                default:
                    alert("Invalid choice: " + docType);
                    return;
            }
            const importafr = unwrapResult(response);
            if (importafr != null) {
                await dispatch(setToImportedAfr(importafr.afr));
                const vc: ValidationContext = {
                    afr: importafr.afr,
                    entity: entity,
                    afrEvents: currentEvents,
                    pendingTableFacts: [],
                    pendingReplaceTableFacts: null,
                    dimensionSectionMap: dMap
                };
                /* check if there are error to notify */
                var errors = getValidationsForAfr(vc);
                console.log("errors", errors);
                if (errors && errors.length > 0) {
                    dispatch(validateAfr(vc));
                }

                Swal.fire('Upload Successful', '', 'info');

                var doc = importafr.report;
                if (doc != null) {
                    //TODO make this a generic function in document slice
                    //that takes mime type, base64 encoded string, filename
                    //and returns a blob.
                    //TODO expand to handle xhtml and excel files
                    var url = "data:" + doc.mimeType + ";base64," + doc.content;
                    fetch(url)
                        .then(res => res.blob())
                        .then(otho => {
                            const data = window.URL.createObjectURL(otho);
                            var link = document.createElement("a");
                            link.href = data;
                            link.download = doc.uri;
                            link.click();
                            setTimeout(function () {
                                // For Firefox it is necessary to delay revoking the ObjectURL
                                window.URL.revokeObjectURL(data);
                            }, 100);
                        });

                    //turn on the dirty flag
                    dispatch(setIsDirty({ IsDirty: true, Page: "" }))
                }
            } else {
                Swal.fire('Oops...', 'Something went wrong!', 'error')
            }

        }
    }

    async function downloadDocumentTemplateToBrowser(doc: DocumentReference) {
        if (doc != null) {
            var url = "data:" + doc.mimeType + ";base64," + doc.content;
            await fetch(url)
                .then(res => res.blob())
                .then(newBlob => {
                    const data = window.URL.createObjectURL(newBlob);
                    var link = document.createElement("a");
                    link.href = data;
                    //                    console.log(data);
                    link.download = doc.uri; //"TemplateDocument.xlsm";
                    link.click();
                    setTimeout(function () {
                        window.URL.revokeObjectURL(data);
                    })
                });
        }
    }

    async function downloadAfrInstanceToBrowser(doc: DocumentReference) {
        if (doc != null) {
            var url = "data:" + doc.mimeType + ";base64," + doc.content;
            await fetch(url)
                .then(res => res.blob())
                .then(newBlob => {
                    const data = window.URL.createObjectURL(newBlob);
                    var link = document.createElement("a");
                    link.href = data;
                    link.download = doc.uri; //"TemplateDocument.xlsm";
                    link.click();
                    setTimeout(function () {
                        window.URL.revokeObjectURL(data);
                    })
                });
        }
    }

    async function downloadTaxonomyToBrowser(doc: DocumentReference) {
        if (doc != null) {
            var url = "data:" + doc.mimeType + ";base64," + doc.content;
            await fetch(url)
                .then(res => res.blob())
                .then(newBlob => {
                    const data = window.URL.createObjectURL(newBlob);
                    var link = document.createElement("a");
                    link.href = data;
                    link.download = doc.uri; //"TemplateDocument.xlsm";
                    link.click();
                    setTimeout(function () {
                        window.URL.revokeObjectURL(data);
                    })
                });
        }
    }

    React.useEffect(() => {
        const resetWorkflow: Workflow = {
            EXCELSequence: 1,
            XBRLSequence: 1
        }
        dispatch(setWorkflowStatus(resetWorkflow));
    }, []);

    const handleSectionChange = (listItem: ListItem) => {
        var foundIndex = availableSections.findIndex((element) => element.value == listItem.value);
        if (foundIndex > -1) {
            //toggle list item
            availableSections[foundIndex].isChecked = !availableSections[foundIndex].isChecked;
        }
        var choices: string[] = [];
        availableSections.map(li => li.isChecked ? choices.push(li.value + "") : "");
        setSectionChoices(choices);

    }
    React.useEffect(() => {
        var sectionSelections: ListItem[] = [];
        sectionSelections.push({ label: "All", value: "all", isChecked: false });
        setAvailableSections(sectionSelections);
    }, [currentAfr]);

    function isReportFormatSelected(reportFormatValue: string): boolean {
        var result = false;

        if (reportFormatValue) {
            if (selectedReportFormat.value == reportFormatValue) {
                result = true;
            }
        }
        return result;
    }
    function reportFormatUnavailable(value: string) {
        var unavailable: boolean = false;
        switch (value.toLowerCase()) {
            case 'pdf':
                unavailable = true;
                break;
        }
        return unavailable;
    }


    const blankOrPopulated = (<div style={{ width: "100%" }}>
        <ul style={{ width: "100%" }} className='d-flex justify-content-around'>
            <li key={"false"} style={{ display: "inline" }}>
                <Input
                    type="radio"
                    value={"false"}
                    name="blank-or-populated"
                    onChange={(e) => { setTemplatePopulated("false"); }}
                    checked={templatePopulated === "false"} />
                Blank Template
            </li>
            <li key={"true"} style={{ display: "inline" }}>
                <Input
                    type="radio"
                    value={"true"}
                    name="blank-or-populated"
                    onChange={(e) => { setTemplatePopulated("true"); }}
                    checked={templatePopulated === "true"} />
                Prior Year's AFR Completed Template
            </li>
        </ul>
    </div>);

    const reportFormatRender = (<div style={{ width: "100%" }}>
        <ul style={{ width: "100%" }} className='d-flex justify-content-around'>
            {downloadFormats.map(({ value, label }) => (
                <li key={value} style={{ display: "inline" }}>
                    <Input
                        type="radio"
                        value={value}
                        name="report-formats"
                        disabled={reportFormatUnavailable(value)}
                        onChange={(e) => {
                            setSelectedReportFormat({ value: value, label: label });
                        }}
                        checked={isReportFormatSelected(label)} />
                    {label}
                </li>
            ))}
        </ul>
    </div>);



    return (
        <React.Fragment>
            {entity !== null ?
                <div>
                    <h2>Court Reporting data for {entity.name} ({entity.code}), {utilityHelper.getAfrYearDescriptor(currentAfr)}</h2>
                    <Row>
                        <Col>
                            <Row style={{
                                paddingTop: '2em'
                            }}>

                                <Col md='6'>
                                    <h4>Excel</h4>
                                    <hr />
                                    <Card>
                                        <CardHeader
                                            style={{
                                                color: "white",
                                                backgroundColor: "#458BCA",
                                                borderRadius: "0px",
                                                fontWeight: "initial"
                                            }}
                                        >Step 1: Download a Court Reporting 939.185 Template</CardHeader>
                                        <CardBody>
                                            <Row>
                                                <GenericActionTile
                                                    actionType="DOWNLOAD_TEMPLATE"
                                                    clickAction={importExportAction}
                                                    entityName={entity.name}
                                                    entityCode={entity.code}
                                                    afrYear={afrYear}
                                                    tileTitle="AFR Data Template"
                                                    tileDescription="Download a custom AFR Data template"
                                                    icon="bi bi-download"
                                                    isEnabled={true}
                                                    isLoading={isDownloadingAfrTemplate}
                                                />
                                            </Row>
                                            <p>Note: If an Account Code is not available in the template, please submit an Account Code bypass request via the Bypass Tab on the Summary Page</p>
                                            <p style={{ color: "red" }}>Please be sure to not delete any tabs or remove any columns from the template. Do not make any adjustments to the formatting of the template.</p>
                                        </CardBody>
                                    </Card>
                                    <Card>
                                        <CardHeader
                                            style={{
                                                color: "white",
                                                backgroundColor: "#458BCA",
                                                borderRadius: "0px",
                                                fontWeight: "initial"
                                            }}
                                        >Step 2: Upload a Completed AFR Template <br />
                                            Select the applicable box to upload by page or All to upload all pages</CardHeader>
                                        <CardBody>
                                            {isDirtyInfo &&
                                                (<Alert color="danger">
                                                    <p>The AFR has unsaved changes.</p>
                                                    <p><b>The upload is disabled until changes are saved or discarded</b></p>
                                                </Alert>)
                                            }
                                            {availableSections.map((sect) => (
                                                <FormGroup key={sect.value}
                                                    check
                                                    inline
                                                >
                                                    <Input type='checkbox'
                                                        defaultChecked={sect.isChecked}

                                                        value={sect.value}
                                                        onChange={(e) => {
                                                            handleSectionChange(sect);
                                                        }}
                                                    />
                                                    <Label check>
                                                        {sect.label}
                                                    </Label>
                                                </FormGroup>
                                            ))}
                                            <GenericActionTileUploadFile
                                                actionType="UPLOAD_SPREADSHEET"
                                                clickAction={importExportActionUploadedFile}
                                                entityName={entity.name}
                                                entityCode={entity.code}
                                                afrYear={afrYear}
                                                tileTitle="Upload Court Reporting 939.185 Data"
                                                tileDescription="Upload Court Reporting 939.185 Data to create a 939.185 version"
                                                icon="bi bi-arrow-bar-up"
                                                isEnabled={!isDirtyInfo && sectionChoices.length > 0}
                                                uploadedFile={file}
                                                isLoading={isUploadingSpreadsheet}
                                            />
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col>
                                    <Card>
                                        <CardHeader
                                            style={{
                                                color: "white",
                                                backgroundColor: "#458BCA",
                                                borderRadius: "0px",
                                                fontWeight: "initial"
                                            }}
                                        >Optional: Download a Partially Completed AFR for additional editing, or a complete one for your records</CardHeader>
                                        <CardBody>
                                            <Row >
                                                {reportFormatRender}
                                            </Row>
                                            <Row className='d-flex justify-content-center'>
                                                <GenericActionTile
                                                    actionType="DOWNLOAD_CURRENT_AFR"
                                                    clickAction={importExportAction}
                                                    entityName={entity.name}
                                                    entityCode={entity.code}
                                                    afrYear={afrYear}
                                                    tileTitle="AFR Data Template"
                                                    tileDescription=""
                                                    icon="bi bi-download"
                                                    isEnabled={selectedReportFormat.value !== 'UNKNOWN'}
                                                    isLoading={isDownloadingCurrentAfr}
                                                />
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row>
                        <Col>

                        </Col>
                    </Row>
                </div>
                : <div>No entity selected</div>}
        </React.Fragment>
    );
}

export default CR29ImportExportCore;